import React, { useState } from 'react';
import { CreateTemplateRequest } from '../types';
import { apiService } from '../services/api';
import './CreateTemplateModal.css';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPattern: string;
  sectionId: string;
  sectionName: string;
  categoryId: string;
  categoryName: string;
  onTemplateCreated: () => void;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  initialPattern,
  sectionId,
  sectionName,
  categoryId,
  categoryName,
  onTemplateCreated
}) => {
  const [pattern, setPattern] = useState(initialPattern);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pattern.trim()) {
      setError('Pattern is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const request: CreateTemplateRequest = {
        pattern: pattern.trim(),
        section_id: sectionId,
        section_name: sectionName,
        category_id: categoryId,
        category_name: categoryName,
      };

      await apiService.createTemplate(request);
      onTemplateCreated();
      onClose();
    } catch (error) {
      console.error('Error creating template:', error);
      setError(error instanceof Error ? error.message : 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setPattern(initialPattern);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Template</h3>
          <button
            className="modal-close-button"
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="template-info">
            <p><strong>Section:</strong> {sectionName}</p>
            <p><strong>Category:</strong> {categoryName}</p>
          </div>

          <div className="form-group">
            <label htmlFor="pattern">Pattern to match:</label>
            <input
              id="pattern"
              type="text"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Enter text pattern to match in descriptions..."
              disabled={saving}
              autoFocus
            />
            <small className="help-text">
              Future transactions containing this text will be automatically assigned to this section/category.
            </small>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !pattern.trim()}
              className="save-button"
            >
              {saving ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTemplateModal;